import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SubredditDetailPanel from '../components/SubredditDetailPanel';
import AIReportsModal from '../components/AIReportsModal';
import PinsTab from '../components/PinsTab';
import { API } from '../config';
import { notifyError, notify } from '../notify';
import AccountBrowserBar from '../components/AccountBrowserBar';


// account_stats.scraped_at is stored without a time zone (UTC)
const utcDate = (s) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');

// Posts tab: what happened to a post after it went up (removal_state from the
// extension's checks and the 1-day / 2-day readings)
const REMOVAL_BADGE = {
  removed: { label: '⛔ Removed by moderators', color: '#ef4444' },
  filtered: { label: "🚫 Removed by Reddit's filters", color: '#f97316', hint: "Reddit's spam filter, not the sub's moderators" },
  pending: { label: '⏸ Held for mod approval', color: '#f59e0b' },
  flagged: { label: '🗑 Deleted', color: '#888' }
};
// The AutoModerator / mod team comment, without its markdown and bot footer
const cleanModNote = (s) => String(s || '')
  .replace(/\*?I am a bot, and this action was performed automatically[\s\S]*$/i, '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/^u\/([\w-]+):\s*/, '$1: ')
  .replace(/[*>]+/g, '')
  .replace(/\s+/g, ' ')
  .trim();
const FIRST_READ_AFTER_H = 12; // backend/routes/stats.js /posts-due

function Stats({ creatorAccountIds, selectedCreator, role }) {
  // ─── Existing State ───
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountFilter, setAccountFilter] = useState(null); // null = all accounts
  // Current strategy of the selected account, shown as a chip in the header
  const [strategyChip, setStrategyChip] = useState(null);
  useEffect(() => {
    if (!accountFilter) { setStrategyChip(null); return; }
    let alive = true;
    axios.get(`${API}/api/accounts/${accountFilter}/strategy`)
      .then(r => { if (alive) setStrategyChip({ text: r.data.summary, isDefault: r.data.is_default }); })
      .catch(() => { if (alive) setStrategyChip(null); });
    return () => { alive = false; };
  }, [accountFilter]);
  const [timeframe, setTimeframe] = useState('7d');
  const [stats, setStats] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [comparison, setComparison] = useState(null);
  const [creatorStats, setCreatorStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [excludedSubreddits, setExcludedSubreddits] = useState([]);
  const [showExcludeModal, setShowExcludeModal] = useState(false);
  const [excludeSearch, setExcludeSearch] = useState('');
  const [excludeTagFilter, setExcludeTagFilter] = useState([]);
  const [pendingExcluded, setPendingExcluded] = useState([]);
  const [allSubreddits, setAllSubreddits] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showOnlyExcluded, setShowOnlyExcluded] = useState(false);
  const [postsPerPage, setPostsPerPage] = useState(20);
  const [postsPage, setPostsPage] = useState(1);
  const [postSort, setPostSort] = useState('date_desc');

  // ─── New Analytics State ───
  const [activeTab, setActiveTab] = useState('subreddits');
  const [subredditReport, setSubredditReport] = useState([]);
  const [contentScorecard, setContentScorecard] = useState([]);
  const [postingTimes, setPostingTimes] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [subReportSort, setSubReportSort] = useState('views_desc');
  const [contentSort, setContentSort] = useState('upvotes_desc');
  const [linkStats, setLinkStats] = useState(null);
  const [syncingLink, setSyncingLink] = useState(false);
  const [showAIReports, setShowAIReports] = useState(false);
  const [sessionPerformance, setSessionPerformance] = useState([]);
  const [attempts, setAttempts] = useState(null); // /api/analytics/attempts
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [sessionSort, setSessionSort] = useState('date_desc');
  const [expandedSubredditId, setExpandedSubredditId] = useState(null);
  const [editingPostUrl, setEditingPostUrl] = useState(null);
  const [editingPostUrlValue, setEditingPostUrlValue] = useState('');
  const creatorId = selectedCreator?.id;

  // Request counters — a late response for an older creator / account /
  // timeframe must never overwrite the data for the current one.
  const accountsSeqRef = useRef(0);
  const statsSeqRef = useRef(0);
  const creatorStatsSeqRef = useRef(0);
  const analyticsSeqRef = useRef(0);

  // Refresh → the server reads karma (and Top & pinned) from Reddit now; this
  // is the wait for it. readRunRef cancels a wait when the creator changes.
  const [redditRead, setRedditRead] = useState(null); // { done, total }
  const [pinsNonce, setPinsNonce] = useState(0);
  const readRunRef = useRef(0);
  useEffect(() => () => { readRunRef.current++; }, []);

  // Creator switched → the account filter / open rows belong to the old one
  useEffect(() => {
    readRunRef.current++;
    setRedditRead(null);
    setAccountFilter(null);
    setExpandedSessionId(null);
    setExpandedSubredditId(null);
    setEditingPostUrl(null);
  }, [creatorId]);

  // ─── Data Fetching ───

  useEffect(() => {
    fetchAccounts();
    fetchAllSubreddits();
    loadExcluded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorAccountIds]);

  useEffect(() => {
    if (selectedAccount) fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, timeframe]);

  useEffect(() => {
    if (accounts.length > 0) fetchCreatorStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, timeframe, excludedSubreddits]);

  useEffect(() => {
    if (creatorId) fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId, accountFilter, timeframe]);

  const fetchAnalytics = useCallback(async () => {
    if (!creatorId) return;
    const seq = ++analyticsSeqRef.current;
    setAnalyticsLoading(true);
    try {
      const params = new URLSearchParams({ creator_id: creatorId });
      if (accountFilter) params.append('account_id', accountFilter);
      if (timeframe !== 'all') {
        const from = new Date();
        if (timeframe === '24h') from.setDate(from.getDate() - 1);
        else if (timeframe === '7d') from.setDate(from.getDate() - 7);
        else if (timeframe === '30d') from.setDate(from.getDate() - 30);
        params.append('from', from.toISOString());
      }
      const qs = params.toString();

      const linkParams = new URLSearchParams();
      if (creatorId) linkParams.append('creator_id', creatorId);
      if (accountFilter) linkParams.append('account_id', accountFilter);
      if (timeframe !== 'all') {
        const fromDate = new Date();
        if (timeframe === '24h') fromDate.setDate(fromDate.getDate() - 1);
        else if (timeframe === '7d') fromDate.setDate(fromDate.getDate() - 7);
        else if (timeframe === '30d') fromDate.setDate(fromDate.getDate() - 30);
        linkParams.append('from', fromDate.toISOString());
      }

      const [subRes, contentRes, timesRes, linkRes, sessRes, attRes] = await Promise.all([
        axios.get(`${API}/api/analytics/subreddit-report?${qs}`).catch(() => null),
        axios.get(`${API}/api/analytics/content-scorecard?${qs}`).catch(() => null),
        axios.get(`${API}/api/analytics/posting-times?${qs}`).catch(() => null),
        axios.get(`${API}/api/link-stats/summary?${linkParams}`).catch(() => null),
        axios.get(`${API}/api/analytics/session-performance?${qs}`).catch(() => null),
        axios.get(`${API}/api/analytics/attempts?${qs}`).catch(() => null)
      ]);
      if (seq !== analyticsSeqRef.current) return; // superseded

      setSubredditReport(subRes?.data || []);
      setContentScorecard(contentRes?.data || []);
      setPostingTimes(timesRes?.data || null);
      setLinkStats(linkRes?.data || null);
      setSessionPerformance(sessRes?.data || []);
      setAttempts(attRes?.data || null);
    } catch (err) {
      if (seq !== analyticsSeqRef.current) return;
      notifyError(err, 'Could not load analytics');
    }
    setAnalyticsLoading(false);
  }, [creatorId, accountFilter, timeframe]);

  // ─── Existing Fetch Functions ───

  const loadExcluded = () => {
    try {
      const saved = localStorage.getItem('stats_excluded_subreddits');
      if (saved) setExcludedSubreddits(JSON.parse(saved));
    } catch (e) {}
  };

  const saveExcluded = (list) => {
    setExcludedSubreddits(list);
    localStorage.setItem('stats_excluded_subreddits', JSON.stringify(list));
  };

  const fetchAllSubreddits = async () => {
    try {
      const res = await axios.get(`${API}/api/subreddits`);
      setAllSubreddits(res.data);
      const tags = [...new Set(res.data.flatMap(s => s.tags || []))].sort();
      setAllTags(tags);
    } catch (err) {
      notifyError(err, 'Could not load subreddits');
    }
  };

  const fetchAccounts = async () => {
    const seq = ++accountsSeqRef.current;
    try {
      const res = await axios.get(`${API}/api/accounts`);
      if (seq !== accountsSeqRef.current) return;
      const filtered = creatorAccountIds
        ? res.data.filter(a => creatorAccountIds.includes(a.id))
        : res.data;
      setAccounts(filtered);
      if (filtered.length > 0) setSelectedAccount(filtered[0]);
      else {
        // no accounts for this creator — don't keep showing the previous one
        statsSeqRef.current++;
        creatorStatsSeqRef.current++;
        setSelectedAccount(null);
        setStats(null);
        setCreatorStats(null);
      }
    } catch (err) {
      if (seq !== accountsSeqRef.current) return;
      notifyError(err, 'Could not load accounts');
    }
  };

  const fetchStats = async () => {
    if (!selectedAccount) return;
    const seq = ++statsSeqRef.current;
    setLoading(true);
    try {
      const [statsRes, compareRes] = await Promise.all([
        axios.get(`${API}/api/stats/${selectedAccount.id}?timeframe=${timeframe}`),
        axios.get(`${API}/api/stats/${selectedAccount.id}/compare?timeframe=${timeframe}`)
      ]);
      if (seq !== statsSeqRef.current) return;
      setStats(statsRes.data);
      setComparison(compareRes.data);
      setPostsPage(1);
    } catch (err) {
      if (seq !== statsSeqRef.current) return;
      notifyError(err, 'Could not load account stats');
    }
    setLoading(false);
  };

  const fetchCreatorStats = async () => {
    const seq = ++creatorStatsSeqRef.current;
    try {
      const allStats = await Promise.all(
        accounts.map(a => axios.get(`${API}/api/stats/${a.id}?timeframe=${timeframe}`).catch(() => null))
      );
      const combined = { post_karma: 0, comment_karma: 0, follower_count: 0, views: 0, upvotes: 0, posts: 0, prev_karma: 0, prev_comment_karma: 0, prev_followers: 0, prev_views: 0, prev_upvotes: 0 };
const allCompare = await Promise.all(
  accounts.map(a => axios.get(`${API}/api/stats/${a.id}/compare?timeframe=${timeframe}`).catch(() => null))
);
for (const res of allCompare) {
  if (!res) continue;
  combined.prev_views += res.data.previous?.views || 0;
  combined.prev_upvotes += res.data.previous?.upvotes || 0;
}
      for (const res of allStats) {
        if (!res) continue;
        const { account, previousAccount, posts } = res.data;
        if (account) {
          combined.post_karma += account.post_karma || 0;
          combined.comment_karma += account.comment_karma || 0;
          combined.follower_count += account.follower_count || 0;
        }
        if (previousAccount) {
          combined.prev_karma += previousAccount.post_karma || 0;
          combined.prev_comment_karma += previousAccount.comment_karma || 0;
          combined.prev_followers += previousAccount.follower_count || 0;
        }
        for (const post of (posts || [])) {
          if (excludedSubreddits.includes(post.subreddit)) continue;
          combined.views += post.views || 0;
          combined.upvotes += post.upvotes || 0;
          combined.posts += 1;
        }
      }
      if (seq !== creatorStatsSeqRef.current) return;
      setCreatorStats(combined);
    } catch (err) {
      if (seq !== creatorStatsSeqRef.current) return;
      notifyError(err, 'Could not load creator stats');
    }
  };

  // Reloads everything shown. Called through a ref after the wait below, so it
  // uses the current account / timeframe, not the ones from when it started.
  const reloadAll = () => {
    fetchStats();
    fetchCreatorStats();
    fetchAnalytics();
    setPinsNonce(n => n + 1);
  };
  const reloadAllRef = useRef(reloadAll);
  reloadAllRef.current = reloadAll;

  // Karma and Top & pinned come from the server scraper, which reads each
  // account a few times a day; Refresh makes it read the shown accounts now
  // (usually within a minute or two) and waits for it. Followers and post
  // views are visible only to the account itself: the extension reads those.
  // onlyIds: just these accounts (Top & pinned's "Check now"); a click event
  // or nothing: the accounts shown.
  const refreshFromReddit = async (onlyIds) => {
    const run = ++readRunRef.current;
    const ids = Array.isArray(onlyIds) && onlyIds.length ? onlyIds : accountFilter ? [accountFilter] : accounts.map(a => a.id);
    if (!ids.length) { reloadAll(); return; }
    let req;
    try {
      req = (await axios.post(`${API}/api/stats/refresh`, { account_ids: ids })).data;
    } catch (err) {
      notifyError(err, 'Could not ask for a Reddit read');
      reloadAll();
      return;
    }
    const want = req.account_ids || [];
    setRedditRead({ done: 0, total: want.length });
    reloadAll(); // what's already in, while the scraper reads
    const reads = await waitForReads(run, want, req.requested_at);
    if (!reads) return; // creator changed or page left
    setRedditRead(null);
    reloadAllRef.current();
    reportReads(want, reads);
  };

  // Polls until every account is read or 5 minutes pass; null when cancelled.
  // A failed poll is retried on the next tick.
  const waitForReads = async (run, want, since) => {
    let reads = [];
    const until = Date.now() + 5 * 60000;
    while (Date.now() < until) {
      await new Promise(r => setTimeout(r, 8000));
      if (run !== readRunRef.current) return null;
      try {
        reads = (await axios.get(`${API}/api/stats/refresh-status`, { params: { account_ids: want.join(','), since } })).data.accounts || [];
      } catch (e) { continue; }
      if (run !== readRunRef.current) return null;
      const done = reads.filter(x => x.done).length;
      setRedditRead({ done, total: want.length });
      if (done === want.length) break;
    }
    return reads;
  };

  const reportReads = (want, reads) => {
    const nameOf = (id) => 'u/' + String(accounts.find(a => a.id === id)?.reddit_username || '?').replace(/^\/?u\//i, '');
    const failed = reads.filter(x => x.error);
    const waiting = want.length - reads.filter(x => x.done).length;
    if (failed.length) notify(`Not read from Reddit: ${failed.map(x => `${nameOf(x.account_id)} (${x.error})`).join(', ')}`, 'error', 10000);
    if (waiting) notify(`${waiting} account${waiting > 1 ? 's' : ''} not read yet: the scraper is busy. Their numbers update on their own later.`, 'info', 8000);
    if (!failed.length && !waiting) notify(`Read from Reddit (karma, pins, top posts) for ${want.length} account${want.length > 1 ? 's' : ''}`, 'success');
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post from statistics?')) return;
    try {
      await axios.delete(`${API}/api/stats/posts/${postId}`);
      setStats(prev => (prev ? { ...prev, posts: (prev.posts || []).filter(p => p.id !== postId) } : prev));
    } catch (err) {
      notifyError(err, 'Could not delete post');
    }
  };

  const flagRescrape = async (body, onDone) => {
    try {
      const res = await axios.post(`${API}/api/stats/flag-rescrape`, body);
      if (onDone) onDone(res.data);
    } catch (err) {
      notifyError(err, 'Could not flag for re-scraping');
    }
  };

  // Reddit link of a scheduled post → PUT /api/scheduler/<session>/posts/<post>
  const savePostUrl = async (sessionId, postId, url) => {
    if (!sessionId) { notify('Could not find the session of this post — reload and try again.', 'error'); return; }
    const value = (url || '').trim() || null;
    try {
      await axios.put(`${API}/api/scheduler/${sessionId}/posts/${postId}`, { post_url: value });
      setSessionPerformance(prev => prev.map(s => s.id !== sessionId ? s : {
        ...s, posts: (s.posts || []).map(p => p.id === postId ? { ...p, post_url: value } : p)
      }));
      setEditingPostUrl(null);
      fetchAnalytics();
    } catch (err) {
      notifyError(err, 'Could not save the post link');
    }
  };

  // ─── Helpers ───

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    if (current === previous) return 0;
    const change = ((current - previous) / previous) * 100;
    return parseFloat(change.toFixed(1));
  };

  const renderChange = (change) => {
    if (change === null || change === undefined) return null;
    const positive = change >= 0;
    return (
      <span style={{ color: positive ? '#22c55e' : '#ef4444', fontSize: '12px', fontWeight: '600', marginLeft: '6px' }}>
        {positive ? '▲' : '▼'} {Math.abs(change)}%
      </span>
    );
  };

  const renderIndicator = (current, previous) => {
    if (!previous || previous === 0) return <span style={{ color: '#555', fontSize: '14px', marginLeft: '6px' }}>●</span>;
    if (current > previous) return <span style={{ color: '#22c55e', fontSize: '14px', marginLeft: '6px' }}>●</span>;
    if (current < previous) return <span style={{ color: '#ef4444', fontSize: '14px', marginLeft: '6px' }}>●</span>;
    return <span style={{ color: '#555', fontSize: '14px', marginLeft: '6px' }}>●</span>;
  };

  // ─── Derived Data ───

  const filteredPosts = (stats?.posts || []).filter(p => !excludedSubreddits.includes(p.subreddit));

  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (postSort === 'date_desc') return new Date(b.posted_at) - new Date(a.posted_at);
    if (postSort === 'date_asc') return new Date(a.posted_at) - new Date(b.posted_at);
    if (postSort === 'views_desc') return (b.views || 0) - (a.views || 0);
    if (postSort === 'views_asc') return (a.views || 0) - (b.views || 0);
    if (postSort === 'upvotes_desc') return (b.upvotes || 0) - (a.upvotes || 0);
    if (postSort === 'upvotes_asc') return (a.upvotes || 0) - (b.upvotes || 0);
    return 0;
  });

  const postsTotalPages = Math.ceil(sortedPosts.length / postsPerPage);
  const paginatedPosts = sortedPosts.slice((postsPage - 1) * postsPerPage, postsPage * postsPerPage);

  const sortedSubReport = [...subredditReport].sort((a, b) => {
    if (subReportSort === 'views_desc') return b.total_views - a.total_views;
    if (subReportSort === 'upvotes_desc') return b.total_upvotes - a.total_upvotes;
    if (subReportSort === 'avg_upvotes_desc') return b.avg_upvotes - a.avg_upvotes;
    if (subReportSort === 'posts_desc') return b.post_count - a.post_count;
    if (subReportSort === 'breakout_desc') return b.breakout_rate - a.breakout_rate;
    return 0;
  });

  const sortedContent = [...contentScorecard].sort((a, b) => {
    if (contentSort === 'upvotes_desc') return b.total_upvotes - a.total_upvotes;
    if (contentSort === 'views_desc') return b.total_views - a.total_views;
    if (contentSort === 'subs_desc') return b.subreddits_posted - a.subreddits_posted;
    return 0;
  });

  // Dashboard metrics from analytics data
  const dashMetrics = {
    totalPosts: subredditReport.reduce((s, r) => s + r.post_count, 0),
    totalViews: subredditReport.reduce((s, r) => s + r.total_views, 0),
    totalUpvotes: subredditReport.reduce((s, r) => s + r.total_upvotes, 0),
    bestSub: subredditReport.length > 0 ? [...subredditReport].sort((a, b) => b.avg_upvotes - a.avg_upvotes)[0] : null,
    bestHour: postingTimes?.best_hour || null,
    bestDay: postingTimes?.best_day || null,
  };

  const prevAccount = stats?.previousAccount;

  // ─── Styles ───

  const pillStyle = (active) => ({
    padding: '6px 16px', borderRadius: '20px', border: 'none',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    background: active ? '#ff4500' : '#2a2a2a',
    color: active ? '#ffffff' : '#aaaaaa'
  });

  const tabStyle = (active) => ({
    padding: '10px 20px', border: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600', borderRadius: '8px 8px 0 0',
    background: active ? '#1a1a1a' : 'transparent',
    color: active ? '#ff4500' : '#666',
    borderBottom: active ? '2px solid #ff4500' : '2px solid transparent',
    transition: 'all 0.2s'
  });

  const confidenceBadge = (tier) => {
    const styles = {
      no_data: { bg: '#2a2a2a', color: '#666', label: 'New' },
      early: { bg: '#3a2a00', color: '#f59e0b', label: 'Early' },
      reliable: { bg: '#0a2a0a', color: '#22c55e', label: 'Reliable' }
    };
    const s = styles[tier] || styles.no_data;
    return (
      <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
        {s.label}
      </span>
    );
  };

  const trendIcon = (trend) => {
    if (trend === 'rising') return <span style={{ color: '#22c55e', fontSize: '13px' }}>↗</span>;
    if (trend === 'declining') return <span style={{ color: '#ef4444', fontSize: '13px' }}>↘</span>;
    return <span style={{ color: '#666', fontSize: '13px' }}>→</span>;
  };

  const exhaustionBadge = (status) => {
    const styles = {
      fresh: { bg: '#0a2a0a', color: '#22c55e', label: 'Fresh' },
      declining: { bg: '#3a2a00', color: '#f59e0b', label: 'Declining' },
      exhausted: { bg: '#2a0a0a', color: '#ef4444', label: 'Exhausted' },
      unknown: { bg: '#2a2a2a', color: '#666', label: '—' }
    };
    const s = styles[status] || styles.unknown;
    return (
      <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>
        {s.label}
      </span>
    );
  };

  // ─── Render: Dashboard Strip ───

  const renderDashboard = () => (
    <div className="card" style={{ borderLeft: '3px solid #ff4500' }}>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <div className="stat-box">
          <div className="stat-box-value">{formatNumber(dashMetrics.totalPosts)}</div>
          <div className="stat-box-label">Scraped Posts</div>
        </div>
        <div className="stat-box">
  <div className="stat-box-value">
    {formatNumber(dashMetrics.totalViews)}
    {creatorStats && renderIndicator(dashMetrics.totalViews, creatorStats.prev_views)}
  </div>
  <div className="stat-box-label">
    Total Views
    {creatorStats && creatorStats.prev_views > 0 && getChange(dashMetrics.totalViews, creatorStats.prev_views) !== 0 && renderChange(getChange(dashMetrics.totalViews, creatorStats.prev_views))}
  </div>
</div>
<div className="stat-box">
  <div className="stat-box-value">
    {formatNumber(dashMetrics.totalUpvotes)}
    {creatorStats && renderIndicator(dashMetrics.totalUpvotes, creatorStats.prev_upvotes)}
  </div>
  <div className="stat-box-label">
    Total Upvotes
    {creatorStats && creatorStats.prev_upvotes > 0 && getChange(dashMetrics.totalUpvotes, creatorStats.prev_upvotes) !== 0 && renderChange(getChange(dashMetrics.totalUpvotes, creatorStats.prev_upvotes))}
  </div>
</div>
        {creatorStats && (
          <>
            <div className="stat-box">
              <div className="stat-box-value">
                {formatNumber(creatorStats.post_karma)}
                {renderIndicator(creatorStats.post_karma, creatorStats.prev_karma)}
              </div>
              <div className="stat-box-label">
                Post Karma
                {getChange(creatorStats.post_karma, creatorStats.prev_karma) !== null && getChange(creatorStats.post_karma, creatorStats.prev_karma) !== 0 && renderChange(getChange(creatorStats.post_karma, creatorStats.prev_karma))}
              </div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value">
                {formatNumber(creatorStats.follower_count)}
                {renderIndicator(creatorStats.follower_count, creatorStats.prev_followers)}
              </div>
              <div className="stat-box-label">
                Followers
                {getChange(creatorStats.follower_count, creatorStats.prev_followers) !== null && getChange(creatorStats.follower_count, creatorStats.prev_followers) !== 0 && renderChange(getChange(creatorStats.follower_count, creatorStats.prev_followers))}
              </div>
            </div>
          </>
        )}
        {dashMetrics.bestSub && (
          <div className="stat-box">
            <div className="stat-box-value" style={{ fontSize: '16px', color: '#ff4500' }}>{dashMetrics.bestSub.subreddit}</div>
            <div className="stat-box-label">Best Subreddit (avg ▲{dashMetrics.bestSub.avg_upvotes})</div>
          </div>
        )}
        {dashMetrics.bestHour && dashMetrics.totalPosts >= 3 && (
          <div className="stat-box">
            <div className="stat-box-value" style={{ fontSize: '16px' }}>
              {dashMetrics.bestDay?.name?.slice(0, 3) || '—'} {dashMetrics.bestHour.label}
            </div>
            <div className="stat-box-label">Best Posting Window (UTC)</div>
          </div>
        )}
        {linkStats && (linkStats.tracked_link || linkStats.total_views > 0) && (
  <>
    <div className="stat-box">
      <div className="stat-box-value">
        {formatNumber(linkStats.total_views)}
        {renderIndicator(linkStats.total_views, linkStats.prev_total_views)}
      </div>
      <div className="stat-box-label">
        Bio Views
        {getChange(linkStats.total_views, linkStats.prev_total_views) !== null && getChange(linkStats.total_views, linkStats.prev_total_views) !== 0 && renderChange(getChange(linkStats.total_views, linkStats.prev_total_views))}
      </div>
    </div>
    {linkStats.tracked_link && (
      <>
        <div className="stat-box">
          <div className="stat-box-value">
            {formatNumber(linkStats.tracked_link.clicks)}
            {renderIndicator(linkStats.tracked_link.clicks, linkStats.tracked_link.prev_clicks)}
          </div>
          <div className="stat-box-label">
            OF Clicks
            {getChange(linkStats.tracked_link.clicks, linkStats.tracked_link.prev_clicks) !== null && getChange(linkStats.tracked_link.clicks, linkStats.tracked_link.prev_clicks) !== 0 && renderChange(getChange(linkStats.tracked_link.clicks, linkStats.tracked_link.prev_clicks))}
          </div>
        </div>
        <div className="stat-box" style={linkStats.tracked_link.conversion_rate < 90 ? { borderColor: '#ef4444', boxShadow: '0 0 12px rgba(239,68,68,0.3)' } : {}}>
          <div className="stat-box-value" style={linkStats.tracked_link.conversion_rate < 90 ? { color: '#ef4444' } : {}}>
            {Math.min(linkStats.tracked_link.conversion_rate, 100)}%
          </div>
          <div className="stat-box-label">Conversion Rate</div>
        </div>
      </>
    )}
  </>
)}
      </div>
    </div>
  );

  // ─── Render: Subreddits Tab ───

  const renderSubredditsTab = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: '#666' }}>
          {subredditReport.length} subreddits · {subredditReport.filter(s => s.confidence === 'reliable').length} reliable
        </div>
        <select
          value={subReportSort}
          onChange={e => setSubReportSort(e.target.value)}
          style={{ fontSize: '12px', padding: '5px 8px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}
        >
          <option value="views_desc">Most views</option>
          <option value="upvotes_desc">Most upvotes</option>
          <option value="avg_upvotes_desc">Best avg upvotes</option>
          <option value="posts_desc">Most posts</option>
          <option value="breakout_desc">Highest breakout rate</option>
        </select>
      </div>
      {sortedSubReport.length === 0 ? (
        <p className="empty">No subreddit data yet. Scrape some sessions first.</p>
      ) : (
        <div className="list">
          {sortedSubReport.map((sub, idx) => (
            <div key={sub.subreddit_id || idx}>
            <div className="list-item" onClick={() => setExpandedSubredditId(expandedSubredditId === sub.subreddit_id ? null : sub.subreddit_id)} style={{ alignItems: 'flex-start', padding: '14px 16px', cursor: 'pointer' }}>
              <div className="list-item-main" style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <strong style={{ color: '#ff4500' }}>#{idx + 1} {sub.subreddit}</strong>
                  {confidenceBadge(sub.confidence)}
                  {trendIcon(sub.trend)}
                </div>
                <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '12px', color: '#888', flexWrap: 'wrap' }}>
                  <span>{sub.post_count} posts</span>
                  <span>{sub.unique_content} content pieces</span>
                  {sub.accounts_used > 1 && <span>{sub.accounts_used} accounts</span>}
                  {sub.breakout_rate > 0 && <span style={{ color: '#f59e0b' }}>🔥 {sub.breakout_rate}% breakout</span>}
                  {sub.weekly_visitors > 0 && <span>👀 {formatNumber(sub.weekly_visitors)} weekly</span>}
                  {sub.tier_distribution && (
                    <span style={{ display: 'flex', gap: '4px' }}>
                      {sub.tier_distribution.viral > 0 && <span style={{ color: '#ef4444' }}>🔥{sub.tier_distribution.viral}</span>}
                      {sub.tier_distribution.strong > 0 && <span style={{ color: '#f59e0b' }}>⭐{sub.tier_distribution.strong}</span>}
                      {sub.tier_distribution.solid > 0 && <span style={{ color: '#22c55e' }}>✓{sub.tier_distribution.solid}</span>}
                      {sub.tier_distribution.weak > 0 && <span style={{ color: '#555' }}>↓{sub.tier_distribution.weak}</span>}
                    </span>
                  )}
                  <span>Last: {sub.last_posted ? new Date(sub.last_posted).toLocaleDateString() : '—'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', fontSize: '13px', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#fff', fontWeight: '600' }}>👁 {formatNumber(sub.avg_views)}</div>
                  <div style={{ color: '#666', fontSize: '11px' }}>avg · best {formatNumber(sub.best_views)}</div>
                </div>
                {sub.avg_penetration !== null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: sub.avg_penetration >= 1 ? '#22c55e' : sub.avg_penetration >= 0.5 ? '#f59e0b' : '#888', fontWeight: '600' }}>{sub.avg_penetration}%</div>
                    <div style={{ color: '#666', fontSize: '11px' }}>penetration</div>
                  </div>
                )}
                {sub.avg_velocity !== null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#aaa' }}>⚡ {formatNumber(sub.avg_velocity)}/h</div>
                    <div style={{ color: '#666', fontSize: '11px' }}>velocity</div>
                  </div>
                )}
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#aaa' }}>▲ {formatNumber(sub.avg_upvotes)}</div>
                  <div style={{ color: '#666', fontSize: '11px' }}>avg upvotes</div>
                </div>
              </div>
            </div>
            {expandedSubredditId === sub.subreddit_id && (
              <SubredditDetailPanel
                subredditId={sub.subreddit_id}
                creatorId={creatorId}
                accountId={accountFilter}
                onClose={() => setExpandedSubredditId(null)}
              />
            )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── Render: Content Tab ───

  const renderContentTab = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ fontSize: '13px', color: '#666' }}>
          {contentScorecard.length} content pieces
        </div>
        <select
          value={contentSort}
          onChange={e => setContentSort(e.target.value)}
          style={{ fontSize: '12px', padding: '5px 8px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}
        >
          <option value="upvotes_desc">Most upvotes</option>
          <option value="views_desc">Most views</option>
          <option value="subs_desc">Most subreddits</option>
        </select>
      </div>
      {sortedContent.length === 0 ? (
        <p className="empty">No content data yet. Scrape some sessions first.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {sortedContent.map((item) => (
            <div key={item.vault_item_id} style={{
              background: '#1a1a1a', borderRadius: '10px', border: '1px solid #2a2a2a',
              overflow: 'hidden', transition: 'border-color 0.2s'
            }}>
              {/* Thumbnail */}
              <div style={{ height: '160px', background: '#0f0f0f', position: 'relative' }}>
                {item.thumbnail_url ? (
                  <img src={item.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#333' }}>No preview</div>
                )}
                {/* Media type badge */}
                <div style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.7)', color: '#aaa', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>
                  {item.carousel_only ? '📸 CAROUSEL' : (item.media_type || 'image').toUpperCase()}
{item.carousel_posts > 0 && !item.carousel_only ? ` · ${item.carousel_posts} carousel` : ''}
                </div>
                {/* Exhaustion badge */}
                {item.exhaustion !== 'unknown' && (
                  <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
                    {exhaustionBadge(item.exhaustion)}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div style={{ padding: '12px' }}>
                {/* Tags */}
                {(item.content_tags || []).length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    {(item.content_tags || []).map(tag => (
                      <span key={tag} style={{ fontSize: '10px', background: '#2a2a2a', color: '#aaa', padding: '2px 8px', borderRadius: '10px' }}>{tag}</span>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>▲ {formatNumber(item.total_upvotes)}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>upvotes · avg {item.avg_upvotes}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#aaa' }}>👁 {formatNumber(item.total_views)}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>views · avg {formatNumber(item.avg_views)}</div>
                  </div>
                </div>

                <div style={{ fontSize: '12px', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{item.post_count} posts · {item.subreddits_posted} subs</span>
                  {item.best_subreddit && (
                    <span style={{ color: '#ff4500' }}>Best: {item.best_subreddit.name}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─── Render: Timing Tab ───

  const renderTimingTab = () => {
    if (!postingTimes || postingTimes.total_posts === 0) {
      return <p className="empty">No timing data yet. Scrape some sessions first.</p>;
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const maxAvg = Math.max(...(postingTimes.grid || []).map(g => g.avg_views), 1);

    const getCellColor = (value) => {
      if (value === 0) return 'transparent';
      const intensity = Math.min(value / maxAvg, 1);
      const r = Math.round(255 * intensity);
      const g = Math.round(69 * intensity);
      return `rgba(${r}, ${g}, 0, ${0.3 + intensity * 0.7})`;
    };

    const gridMap = {};
    for (const cell of (postingTimes.grid || [])) {
      gridMap[`${cell.day}-${cell.hour}`] = cell;
    }

    return (
      <div>
        {/* Best windows */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {postingTimes.best_day && (
            <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '12px 20px', flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>BEST DAY</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#ff4500' }}>{postingTimes.best_day.name}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>{postingTimes.best_day.posts} posts · avg ▲{postingTimes.best_day.avg_upvotes}</div>
            </div>
          )}
          {postingTimes.best_hour && (
            <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '12px 20px', flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>BEST HOUR (UTC)</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#ff4500' }}>{postingTimes.best_hour.label}</div>
              <div style={{ fontSize: '12px', color: '#888' }}>{postingTimes.best_hour.posts} posts · avg ▲{postingTimes.best_hour.avg_upvotes}</div>
            </div>
          )}
          <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '12px 20px', flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>TOTAL SCRAPED</div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>{postingTimes.total_posts}</div>
            <div style={{ fontSize: '12px', color: '#888' }}>posts with analytics data</div>
          </div>
        </div>

        {/* Hour × Day heatmap grid */}
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: '700px' }}>
            {/* Hour headers */}
            <div style={{ display: 'flex', marginLeft: '40px', marginBottom: '4px' }}>
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ flex: 1, textAlign: 'center', fontSize: '10px', color: '#555' }}>
                  {h % 3 === 0 ? `${h}` : ''}
                </div>
              ))}
            </div>

            {/* Grid rows */}
            {Array.from({ length: 7 }, (_, d) => (
              <div key={d} style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                <div style={{ width: '40px', fontSize: '11px', color: '#888', textAlign: 'right', paddingRight: '8px' }}>
                  {dayNames[d]}
                </div>
                {Array.from({ length: 24 }, (_, h) => {
                  const cell = gridMap[`${d}-${h}`];
                  return (
                    <div
                      key={h}
                      title={cell ? `${dayNames[d]} ${h}:00 — ${cell.posts} posts, avg ▲${cell.avg_upvotes}, avg 👁${cell.avg_views}` : `${dayNames[d]} ${h}:00 — no data`}
                      style={{
                        flex: 1, height: '28px', margin: '1px',
                        borderRadius: '3px',
                        background: cell ? getCellColor(cell.avg_views) : '#1a1a1a',
                        border: cell ? '1px solid rgba(255,69,0,0.2)' : '1px solid #222',
                        cursor: 'default'
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* By-hour bar chart */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ fontSize: '14px', color: '#aaa', marginBottom: '12px' }}>Performance by Hour (UTC)</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px' }}>
            {postingTimes.by_hour.map(h => {
              const barHeight = h.posts > 0 ? Math.max((h.avg_views / (postingTimes.best_hour?.avg_views || 1)) * 100, 8) : 0;
              return (
                <div key={h.hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                  {h.posts > 0 && (
                    <div style={{ fontSize: '9px', color: '#888', marginBottom: '2px' }}>{formatNumber(h.avg_views)}</div>
                  )}
                  <div
                    title={`${h.label}: ${h.posts} posts, avg ▲${h.avg_upvotes}, avg 👁${h.avg_views}`}
                    style={{
                      width: '100%', maxWidth: '20px',
                      height: `${barHeight}%`,
                      background: h.posts > 0 ? '#ff4500' : '#1a1a1a',
                      borderRadius: '3px 3px 0 0',
                      opacity: h.posts > 0 ? 0.5 + (h.avg_upvotes / (postingTimes.best_hour?.avg_upvotes || 1)) * 0.5 : 0.2
                    }}
                  />
                  <div style={{ fontSize: '9px', color: '#555', marginTop: '4px' }}>
                    {h.hour % 3 === 0 ? h.hour : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ─── Render: Sessions Tab ───

  // ─── Attempts: what happened to every planned post ───
  // Published / still live at 2 days / removed (Reddit's filters, moderators,
  // held by AutoModerator) / failed with a reason / skipped. A rate needs at
  // least 5 posts behind it; below that the raw count is shown.
  const renderAttemptsTab = () => {
    const a = attempts;
    if (!a || !a.total || a.total.planned === 0) {
      return <p className="empty">No posting runs in this period yet.</p>;
    }
    const t = a.total;
    const rate = (num, den) => den >= 5 ? `${Math.round(num / den * 100)}%` : (den ? `${num} of ${den}` : '—');
    const removedLabel = { filtered: "Reddit's filters", removed: 'moderators', pending: 'held by AutoMod', flagged: 'admins / deleted' };
    const tile = (label, value, sub, color) => (
      <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '10px', padding: '12px 14px', minWidth: '130px', flex: '1 1 130px' }}>
        <div style={{ fontSize: '11px', color: '#777', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: '20px', fontWeight: 700, color: color || '#fff', marginTop: '4px' }}>{value}</div>
        {sub && <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{sub}</div>}
      </div>
    );
    const reasons = Object.entries(t.failed_by || {}).sort((x, y) => y[1] - x[1]);
    const removedParts = Object.entries(t.removed_by || {}).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${removedLabel[k] || k}`);
    const cell = { padding: '7px 8px', borderBottom: '1px solid #222', fontSize: '12px', color: '#ccc', whiteSpace: 'nowrap' };
    const head = { ...cell, color: '#777', fontWeight: 600, fontSize: '11px' };
    return (
      <div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {tile('Planned', t.planned, `${t.skipped} skipped`)}
          {tile('Published', t.published, `${rate(t.published, t.published + t.failed)} of tries`, '#fff')}
          {tile('Live at 2 days', t.live_final, `${rate(t.live_final, t.live_final + t.removed)} survived · ${t.live_early} not at 2 days yet`, '#22c55e')}
          {tile('Removed', t.removed, removedParts.join(' · ') || 'none recorded', t.removed ? '#ef4444' : '#fff')}
          {tile('Failed', t.failed, reasons.slice(0, 2).map(([r, n]) => `${n} ${r}`).join(' · ') || 'none', t.failed ? '#f59e0b' : '#fff')}
          {tile('Unchecked', t.unchecked, 'published before status checks', '#777')}
        </div>

        {(a.modes || []).some(m => m.mode === 'background') && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '6px' }}>Background mode vs normal (experiment)</div>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...head, textAlign: 'left' }}>Mode</th><th style={head}>Published</th><th style={head}>Removed by filters</th>
                <th style={head}>Removed by mods</th><th style={head}>Failed</th><th style={head}>Survived 2 days</th><th style={head}>Removal rate</th>
              </tr></thead>
              <tbody>
                {a.modes.filter(m => m.mode !== 'not recorded').map(m => (
                  <tr key={m.mode}>
                    <td style={{ ...cell, color: '#fff' }}>{m.mode === 'background' ? '🧪 background' : 'normal (window in front)'}</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{m.published}</td>
                    <td style={{ ...cell, textAlign: 'center', color: m.removed_by?.filtered ? '#ef4444' : '#555' }}>{m.removed_by?.filtered || 0}</td>
                    <td style={{ ...cell, textAlign: 'center', color: m.removed_by?.removed ? '#ef4444' : '#555' }}>{m.removed_by?.removed || 0}</td>
                    <td style={{ ...cell, textAlign: 'center', color: m.failed ? '#f59e0b' : '#555' }}>{m.failed}</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{rate(m.live_final, m.live_final + m.removed)}</td>
                    <td style={{ ...cell, textAlign: 'center' }}>{rate(m.removed, m.live_final + m.live_early + m.removed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {reasons.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '6px' }}>Why posts failed</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {reasons.map(([r, n]) => (
                <span key={r} style={{ fontSize: '11px', background: '#2a1f00', color: '#f59e0b', border: '1px solid #92400e', borderRadius: '10px', padding: '2px 9px' }}>{r} · {n}</span>
              ))}
            </div>
          </div>
        )}

        {(a.removals || []).length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '6px' }}>Removed after posting</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {a.removals.slice(0, 20).map(r => (
                <div key={r.scheduled_post_id} style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderLeft: '3px solid #ef4444', borderRadius: '8px', padding: '8px 12px' }}>
                  <div style={{ fontSize: '12px', color: '#fff' }}>
                    <strong>{r.subreddit}</strong>
                    <span style={{ color: '#ef4444' }}> · {r.text || removedLabel[r.state] || r.state}</span>
                    {r.account && <span style={{ color: '#666' }}> · u/{r.account}</span>}
                    {r.posted_at && <span style={{ color: '#666' }}> · {new Date(r.posted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
                    {r.post_url ? <a href={r.post_url} target="_blank" rel="noreferrer" style={{ color: '#aaa' }}>{r.title || '(no title)'}</a> : (r.title || '(no title)')}
                  </div>
                  {r.note && <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px', fontStyle: 'italic' }}>{r.note}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '6px' }}>By subreddit</div>
        <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...head, textAlign: 'left' }}>Subreddit</th>
              <th style={head}>Planned</th><th style={head}>Published</th><th style={head}>Live 2d</th>
              <th style={head}>Removed</th><th style={head}>Failed</th><th style={head}>Skipped</th>
              <th style={{ ...head, textAlign: 'left' }}>Last problem</th>
            </tr></thead>
            <tbody>
              {(a.subs || []).map(sb => (
                <tr key={sb.subreddit_id || sb.name}>
                  <td style={{ ...cell, color: '#fff' }}>{sb.name}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{sb.planned}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{sb.published}</td>
                  <td style={{ ...cell, textAlign: 'center', color: sb.live_final ? '#22c55e' : '#555' }}>{sb.live_final}</td>
                  <td style={{ ...cell, textAlign: 'center', color: sb.removed ? '#ef4444' : '#555' }}>{sb.removed}</td>
                  <td style={{ ...cell, textAlign: 'center', color: sb.failed ? '#f59e0b' : '#555' }}>{sb.failed}</td>
                  <td style={{ ...cell, textAlign: 'center', color: '#555' }}>{sb.skipped}</td>
                  <td style={{ ...cell, whiteSpace: 'normal', maxWidth: '360px', color: '#aaa' }} title={sb.last_note || ''}>
                    {sb.last_problem || ''}{sb.last_note ? ` — ${sb.last_note.slice(0, 90)}${sb.last_note.length > 90 ? '…' : ''}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: '13px', fontWeight: 600, color: '#fff', marginBottom: '6px' }}>By session</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...head, textAlign: 'left' }}>Date</th><th style={{ ...head, textAlign: 'left' }}>Account</th>
              <th style={head}>Planned</th><th style={head}>Published</th><th style={head}>Removed</th>
              <th style={head}>Failed</th><th style={head}>Skipped</th><th style={head}>Published of tries</th>
            </tr></thead>
            <tbody>
              {(a.sessions || []).slice(0, 60).map(ss => (
                <tr key={ss.session_id}>
                  <td style={cell}>{ss.scheduled_date}</td>
                  <td style={cell}>{ss.account ? `u/${ss.account}` : ''}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{ss.planned}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{ss.published}</td>
                  <td style={{ ...cell, textAlign: 'center', color: ss.removed ? '#ef4444' : '#555' }}>{ss.removed}</td>
                  <td style={{ ...cell, textAlign: 'center', color: ss.failed ? '#f59e0b' : '#555' }}>{ss.failed}</td>
                  <td style={{ ...cell, textAlign: 'center', color: '#555' }}>{ss.skipped}</td>
                  <td style={{ ...cell, textAlign: 'center' }}>{rate(ss.published, ss.published + ss.failed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {a.note && <div style={{ fontSize: '11px', color: '#666', marginTop: '10px' }}>{a.note}</div>}
      </div>
    );
  };

  const renderSessionsTab = () => {
    const sorted = [...sessionPerformance].sort((a, b) => {
      if (sessionSort === 'date_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sessionSort === 'date_asc') return new Date(a.created_at) - new Date(b.created_at);
      if (sessionSort === 'views_desc') return b.total_views - a.total_views;
      if (sessionSort === 'upvotes_desc') return b.total_upvotes - a.total_upvotes;
      if (sessionSort === 'posts_desc') return b.total_posts - a.total_posts;
      return 0;
    });

    if (sorted.length === 0) {
      return <p className="empty">No completed sessions yet. Sessions appear here after posting and scraping.</p>;
    }

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {sorted.length} sessions · {sorted.reduce((s, sess) => s + sess.scraped_posts, 0)} scraped posts
          </div>
          <select
            value={sessionSort}
            onChange={e => setSessionSort(e.target.value)}
            style={{ fontSize: '12px', padding: '5px 8px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}
          >
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
            <option value="views_desc">Most views</option>
            <option value="upvotes_desc">Most upvotes</option>
            <option value="posts_desc">Most posts</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {sorted.map(sess => {
            const isExpanded = expandedSessionId === sess.id;
            const hasProfile = sess.profile_posts > 0;

            return (
              <div key={sess.id} style={{
                background: '#1a1a1a', borderRadius: '10px', border: '1px solid #2a2a2a',
                overflow: 'hidden', transition: 'border-color 0.2s'
              }}>
                {/* Session header */}
                <div
                  onClick={() => setExpandedSessionId(isExpanded ? null : sess.id)}
                  style={{
                    padding: '14px 16px', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderBottom: isExpanded ? '1px solid #2a2a2a' : 'none'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '14px', color: '#fff' }}>{sess.name || 'Untitled Session'}</strong>
                      <span style={{ fontSize: '11px', color: '#666' }}>u/{sess.reddit_username}</span>
                      {hasProfile && <span style={{ fontSize: '11px', color: '#8b5cf6' }}>📌 profile</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      {new Date(sess.scheduled_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {' · '}{sess.regular_posts} posts
                      {sess.scraped_posts < sess.regular_posts && (
                        <span style={{ color: '#f59e0b' }}> · {sess.scraped_posts} scraped</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#fff' }}>👁 {formatNumber(sess.total_views)}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>avg {formatNumber(sess.avg_views)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#22c55e' }}>▲ {formatNumber(sess.total_upvotes)}</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>avg {sess.avg_upvotes}</div>
                    </div>
                    <span style={{ color: '#555', fontSize: '14px', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>▼</span>
                    {isExpanded && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          flagRescrape({ session_id: sess.id }, d => alert(`🔄 ${d.flagged} posts flagged for re-scraping`));
                        }}
                        style={{ fontSize: '10px', padding: '4px 10px', background: 'transparent', color: '#8b5cf6', border: '1px solid #7c3aed', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >🔄 Re-scrape session</button>
                    )}
                  </div>
                </div>

                {/* Expanded posts */}
                {isExpanded && (
                  <div style={{ padding: '12px 16px' }}>
                    {/* Profile post summary if exists */}
                    {hasProfile && sess.profile_views > 0 && (
                      <div style={{
                        padding: '8px 12px', background: '#1a0a2a', border: '1px solid #7c3aed',
                        borderRadius: '8px', marginBottom: '12px', fontSize: '12px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}>
                        <span style={{ color: '#a78bfa' }}>📌 Profile post performance</span>
                        <span style={{ color: '#a78bfa' }}>👁 {formatNumber(sess.profile_views)} · ▲ {formatNumber(sess.profile_upvotes)}</span>
                      </div>
                    )}

                    {/* Per-post table */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {sess.posts
                        .sort((a, b) => {
                          if (a.is_profile_post && !b.is_profile_post) return -1;
                          if (!a.is_profile_post && b.is_profile_post) return 1;
                          return (b.views || 0) - (a.views || 0);
                        })
                        .map(post => (
                          <div key={post.id} style={{
                            display: 'flex', gap: '10px', alignItems: 'center',
                            padding: '8px 10px', borderRadius: '6px',
                            background: post.is_profile_post ? '#0f0a1a' : '#0f0f0f',
                            border: post.is_profile_post ? '1px solid #7c3aed' : '1px solid #222'
                          }}>
                            {post.thumbnail_url ? (
                              <img src={post.thumbnail_url} alt="" style={{
                                width: '36px', height: '36px', objectFit: 'cover',
                                borderRadius: '4px', flexShrink: 0
                              }} />
                            ) : (
                              <div style={{ width: '36px', height: '36px', background: '#222', borderRadius: '4px', flexShrink: 0 }} />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12px', color: post.is_profile_post ? '#a78bfa' : '#ff4500', fontWeight: '600' }}>{post.is_profile_post ? '📌 Profile Post' : (post.subreddit || 'unknown')}</div>
                              {editingPostUrl === post.id ? (
                                <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                                  <input value={editingPostUrlValue} onChange={e => setEditingPostUrlValue(e.target.value)}
                                    placeholder="https://www.reddit.com/r/..." autoFocus
                                    style={{ flex: 1, fontSize: '10px', padding: '3px 6px', background: '#0a0a0a', border: '1px solid #333', borderRadius: '4px', color: '#fff' }} />
                                  <button onClick={() => savePostUrl(post.session_id || sess.id, post.id, editingPostUrlValue)} style={{ fontSize: '10px', padding: '3px 6px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                                  <button onClick={() => setEditingPostUrl(null)} style={{ fontSize: '10px', padding: '3px 6px', background: 'transparent', color: '#666', border: '1px solid #333', borderRadius: '4px', cursor: 'pointer' }}>✕</button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '1px' }}>
                                  <div style={{ fontSize: '11px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                    {post.post_url ? (
                                      <a href={post.post_url} target="_blank" rel="noreferrer" style={{ color: '#888', textDecoration: 'none' }}>{post.title || '(no title)'}</a>
                                    ) : (post.title || '(no title)')}
                                  </div>
                                  <button onClick={() => { setEditingPostUrl(post.id); setEditingPostUrlValue(post.post_url || ''); }}
                                    style={{ fontSize: '9px', padding: '1px 4px', background: 'transparent', color: '#555', border: '1px solid #333', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}>✏️</button>
                                  {post.post_url && (
                                    <button onClick={() => flagRescrape({ post_id: post.id }, () => alert('🔄 Flagged for re-scraping'))}
                                      style={{ fontSize: '9px', padding: '1px 4px', background: 'transparent', color: '#555', border: '1px solid #333', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}>🔄</button>
                                  )}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '10px', flexShrink: 0, fontSize: '12px', alignItems: 'center' }}>
                              {post.has_stats ? (
                                post.is_profile_post ? (
                                  <>
                                    <span style={{ color: '#a78bfa' }}>👁 {formatNumber(post.views)}</span>
                                    <span style={{ color: '#a78bfa' }}>▲ {formatNumber(post.upvotes)}</span>
                                  </>
                                ) : (
                                  <>
                                    {post.tier_label && (
                                      <span style={{
                                        fontSize: '10px', fontWeight: '600', padding: '2px 6px', borderRadius: '6px',
                                        background: post.tier === 'viral' ? '#2a0a0a' : post.tier === 'strong' ? '#2a1a00' : post.tier === 'solid' ? '#0a1a0a' : post.tier === 'weak' ? '#1a1a1a' : '#1a1a1a',
                                        color: post.tier === 'viral' ? '#ef4444' : post.tier === 'strong' ? '#f59e0b' : post.tier === 'solid' ? '#22c55e' : post.tier === 'weak' ? '#555' : '#666'
                                      }}>{post.tier_label}</span>
                                    )}
                                    <span style={{ color: '#aaa' }}>👁 {formatNumber(post.views)}</span>
                                    {post.organic_views !== null && post.organic_views !== post.views && (
                                      <span style={{ color: '#888', fontSize: '10px' }}>({formatNumber(post.organic_views)} organic)</span>
                                    )}
                                    {post.penetration !== null && (
                                      <span style={{ color: post.penetration >= 2 ? '#22c55e' : post.penetration >= 0.5 ? '#f59e0b' : '#666', fontSize: '11px' }}>{post.penetration}%</span>
                                    )}
                                    <span style={{ color: (post.upvotes || 0) > 0 ? '#22c55e' : '#555' }}>▲ {formatNumber(post.upvotes)}</span>
                                  </>
                                )
                              ) : (
                                <span style={{ color: '#444', fontSize: '11px' }}>not scraped</span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Render: Posts Tab ───

  const renderPostsTab = () => {
    if (!stats) return <p className="empty">Select an account to see posts.</p>;

    return (
      <div>
        {/* Account selector for posts */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {accounts.map(account => (
            <button
              key={account.id}
              onClick={() => setSelectedAccount(account)}
              style={pillStyle(selectedAccount?.id === account.id)}
            >
              u/{account.reddit_username}
            </button>
          ))}
        </div>

        {/* Account Overview */}
        {stats.account && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div className="stat-box">
              <div className="stat-box-value">{formatNumber(stats.account.post_karma)}{renderIndicator(stats.account.post_karma, prevAccount?.post_karma)}</div>
              <div className="stat-box-label">Post Karma{getChange(stats.account.post_karma, prevAccount?.post_karma) !== null && getChange(stats.account.post_karma, prevAccount?.post_karma) !== 0 && renderChange(getChange(stats.account.post_karma, prevAccount?.post_karma))}</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value">{formatNumber(stats.account.comment_karma)}{renderIndicator(stats.account.comment_karma, prevAccount?.comment_karma)}</div>
              <div className="stat-box-label">Comment Karma{getChange(stats.account.comment_karma, prevAccount?.comment_karma) !== null && getChange(stats.account.comment_karma, prevAccount?.comment_karma) !== 0 && renderChange(getChange(stats.account.comment_karma, prevAccount?.comment_karma))}</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value">{stats.account.follower_count == null ? '—' : formatNumber(stats.account.follower_count)}{renderIndicator(stats.account.follower_count, prevAccount?.follower_count)}</div>
              <div className="stat-box-label">Followers{getChange(stats.account.follower_count, prevAccount?.follower_count) !== null && getChange(stats.account.follower_count, prevAccount?.follower_count) !== 0 && renderChange(getChange(stats.account.follower_count, prevAccount?.follower_count))}</div>
            </div>
          </div>
        )}

        {filteredPosts.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>{filteredPosts.length} posts</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select value={postSort} onChange={e => { setPostSort(e.target.value); setPostsPage(1); }}
                  style={{ fontSize: '12px', padding: '5px 8px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}>
                  <option value="date_desc">Newest first</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="views_desc">Most views</option>
                  <option value="views_asc">Least views</option>
                  <option value="upvotes_desc">Most upvotes</option>
                  <option value="upvotes_asc">Least upvotes</option>
                </select>
                <select value={postsPerPage} onChange={e => { setPostsPerPage(parseInt(e.target.value)); setPostsPage(1); }}
                  style={{ fontSize: '12px', padding: '5px 8px', background: '#1a1a1a', color: '#aaaaaa', border: '1px solid #333', borderRadius: '6px' }}>
                  {[10, 20, 50, 100].map(n => (<option key={n} value={n}>{n} / page</option>))}
                </select>
              </div>
            </div>
            <div className="list">
              {paginatedPosts.map(post => {
                const badge = REMOVAL_BADGE[post.removal_state] || null;
                const note = badge ? cleanModNote(post.removal_note) : '';
                const read = (post.views || 0) > 0;
                const postedAt = post.posted_at ? utcDate(post.posted_at) : null;
                const firstReadAt = postedAt ? new Date(+postedAt + FIRST_READ_AFTER_H * 3600e3) : null;
                // no views yet: why
                const unread = read ? null
                  : badge && post.removal_state !== 'pending' ? { text: 'no views', title: 'Reddit shows no views on a removed post; it counts as 0' }
                  : !post.post_url ? { text: 'no post link', title: 'The post was never found after posting, so it cannot be read' }
                  : firstReadAt && firstReadAt > new Date() ? { text: `⏳ 1st reading from ${firstReadAt.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}`, title: `Views are first read ${FIRST_READ_AFTER_H} h after posting, then again at 2 days` }
                  : { text: '⏳ not read yet', title: 'Due for its views reading: run Update post stats' };
                return (
                <div className="list-item" key={post.id}>
                  <div className="list-item-main" style={{ minWidth: 0 }}>
                    <strong>
                      {post.post_url
                        ? <a href={post.post_url} target="_blank" rel="noreferrer" style={{ color: badge && post.removal_state !== 'pending' ? '#9a3412' : '#ff4500', textDecoration: 'none' }}>{post.title || '(no title)'}</a>
                        : (post.title || '(no title)')
                      }
                    </strong>
                    <span className="sub">
                      <a href={`https://www.reddit.com/${post.subreddit}`} target="_blank" rel="noreferrer" style={{ color: '#aaaaaa', textDecoration: 'none' }}>{post.subreddit}</a>
                      {' · '}{new Date(post.posted_at).toLocaleDateString()}
                      {badge && (
                        <span title={[badge.hint, post.removal_checked_at ? `checked ${utcDate(post.removal_checked_at).toLocaleString()}` : null].filter(Boolean).join(' · ')}
                          style={{ marginLeft: '8px', color: badge.color, fontWeight: 600 }}>
                          {post.removal_state === 'pending' && post.removal_text ? `⏸ ${post.removal_text}` : badge.label}
                        </span>
                      )}
                    </span>
                    {note && (
                      <div title={note} style={{ fontSize: '12px', color: '#999', marginTop: '4px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        💬 {note}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '13px', alignItems: 'center', flexShrink: 0 }}>
                    {unread ? (
                      <span title={unread.title} style={{ color: '#666', fontSize: '12px' }}>{unread.text}</span>
                    ) : (
                      <>
                        <span style={{ color: '#ffffff' }}>👁 {formatNumber(post.views)}</span>
                        <span style={{ color: (post.upvotes || 0) > 0 ? '#22c55e' : (post.upvotes || 0) < 0 ? '#ef4444' : '#555' }}>▲ {formatNumber(post.upvotes)}</span>
                      </>
                    )}
                    <button onClick={() => flagRescrape({ post_id: post.id }, () => alert('🔄 Flagged for re-scraping'))}
                      style={{ background: 'transparent', border: '1px solid #333', color: '#555', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}
                      onMouseEnter={e => { e.target.style.borderColor = '#8b5cf6'; e.target.style.color = '#8b5cf6'; }}
                      onMouseLeave={e => { e.target.style.borderColor = '#333'; e.target.style.color = '#555'; }}
                      title="Flag for re-scraping">🔄</button>
                    {role === 'admin' && (
                      <button onClick={() => deletePost(post.id)}
                        style={{ background: 'transparent', border: '1px solid #333', color: '#555', borderRadius: '6px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}
                        onMouseEnter={e => { e.target.style.borderColor = '#ef4444'; e.target.style.color = '#ef4444'; }}
                        onMouseLeave={e => { e.target.style.borderColor = '#333'; e.target.style.color = '#555'; }}>×</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            {postsTotalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
                <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} disabled={postsPage === 1} onClick={() => setPostsPage(p => p - 1)}>← Prev</button>
                <span style={{ color: '#aaaaaa', fontSize: '12px' }}>{postsPage} of {postsTotalPages}</span>
                <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} disabled={postsPage === postsTotalPages} onClick={() => setPostsPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}
        {stats.account?.scraped_at && (
          <div style={{ fontSize: '11px', color: '#555', marginTop: '12px' }}>
            Karma read {utcDate(stats.account.scraped_at).toLocaleString()}
            {' · '}
            {stats.account.followers_at
              ? `followers read ${utcDate(stats.account.followers_at).toLocaleString()} by the extension`
              : 'followers not read yet: the extension reads them after posting runs and Update Post Stats'}
          </div>
        )}
      </div>
    );
  };

  // ─── Exclude Modal ───

  const openExcludeModal = () => {
    setPendingExcluded([...excludedSubreddits]);
    setExcludeSearch('');
    setExcludeTagFilter([]);
    setShowOnlyExcluded(false);
    setShowExcludeModal(true);
  };

  const confirmExclude = () => {
    saveExcluded(pendingExcluded);
    setShowExcludeModal(false);
  };

  const filteredSubredditsForModal = allSubreddits.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(excludeSearch.toLowerCase());
    const matchTag = excludeTagFilter.length === 0 || excludeTagFilter.every(t => (s.tags || []).includes(t));
    const matchExcluded = !showOnlyExcluded || pendingExcluded.includes(s.name);
    return matchSearch && matchTag && matchExcluded;
  }).sort((a, b) => a.name.localeCompare(b.name));

  // ─── Main Render ───

  const tabs = [
    { id: 'subreddits', label: 'Subreddits' },
    { id: 'content', label: 'Content' },
    { id: 'sessions', label: 'Sessions' },
    { id: 'attempts', label: '🎯 Attempts' },
    { id: 'timing', label: 'Timing' },
    { id: 'pins', label: '📌 Top & pinned' },
    { id: 'posts', label: 'Posts' }
  ];

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Statistics</h1>
          <p>{selectedCreator?.name || 'Creator'} — Performance Dashboard</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={openExcludeModal} className="btn-secondary" style={{ fontSize: '13px' }}>
            Exclude Subreddits {excludedSubreddits.length > 0 ? `(${excludedSubreddits.length})` : ''}
          </button>
          <button className="btn-secondary" onClick={() => setScraping(!scraping)}
            style={{ background: 'transparent', border: '1px solid #444', color: '#aaa', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer' }}>
            {scraping ? 'Dismiss' : 'Update Stats'}
          </button>
        </div>
      </div>

      {scraping && (
        <div className="card" style={{ borderColor: '#ff4500' }}>
          <h2>Update Stats</h2>
          <p style={{ color: '#aaaaaa', fontSize: '13px' }}>
            Karma and Top &amp; pinned are read from Reddit automatically a few times a day; <strong>Refresh</strong> reads them right away.
            Post views and followers are visible only to the account itself: open the extension in the Dolphin Anty profile for <strong>{selectedAccount?.name}</strong> and click <strong>Update Post Stats (last 2 days)</strong> (posting runs read the followers too).
          </p>
        </div>
      )}

      {/* Timeframe + Account filter */}
      <div className="card">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          {['24h', '7d', '30d', 'all'].map(tf => (
            <button key={tf} onClick={() => { setTimeframe(tf); setPostsPage(1); }} style={pillStyle(timeframe === tf)}>
              {tf === '24h' ? 'Last 24h' : tf === '7d' ? 'Last 7 days' : tf === '30d' ? 'Last 30 days' : 'All Time'}
            </button>
          ))}

          <span style={{ color: '#333', margin: '0 4px' }}>|</span>

          <button onClick={() => { setAccountFilter(null); }} style={pillStyle(accountFilter === null)}>
            All Accounts
          </button>
          {accounts.map(account => (
            <button key={account.id} onClick={() => { setAccountFilter(account.id); setSelectedAccount(account); }}
              style={pillStyle(accountFilter === account.id)}>
              u/{account.reddit_username}
            </button>
          ))}

          {strategyChip && (
            <span title="This account's strategy — edit it on the Accounts page" style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '12px', background: '#0a1a2a', color: strategyChip.isDefault ? '#888' : '#60a5fa', border: '1px solid #1e3a5f' }}>
              🎯 {strategyChip.isDefault ? 'default strategy' : strategyChip.text}
            </span>
          )}
          <button onClick={refreshFromReddit} disabled={!!redditRead || loading || analyticsLoading} className="btn-secondary"
            title="Reads karma and Top & pinned from Reddit now (post views and followers come from the extension)"
            style={{ marginLeft: 'auto', fontSize: '13px', padding: '6px 16px' }}>
            {redditRead ? `Reading Reddit… ${redditRead.done}/${redditRead.total}` : loading || analyticsLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={() => setShowAIReports(true)} className="btn-secondary"
            style={{ fontSize: '13px', padding: '6px 16px', borderColor: '#f59e0b', color: '#f59e0b' }}>
            🤖 AI Reports
          </button>
          <button onClick={async () => {
            setSyncingLink(true);
            try {
              // Target the selected account so the sync is fast (syncing ALL
              // accounts could exceed the request timeout → error → the refresh
              // below was skipped, so clicks only appeared after a page reload).
              const body = accountFilter ? { account_id: accountFilter } : {};
              await axios.post(`${API}/api/link-stats/sync`, body);
            } catch (err) {
              notifyError(err, 'Bio link sync failed');
            } finally {
              // Refresh regardless — the backend may have written the data even
              // if the response was slow/errored.
              await fetchAnalytics();
              if (selectedAccount) fetchStats();
              setSyncingLink(false);
            }
          }} disabled={syncingLink} className="btn-secondary"
            style={{ fontSize: '13px', padding: '6px 16px' }}>
            {syncingLink ? 'Syncing...' : '🔗 Sync Bio'}
          </button>
        </div>
      </div>

      {/* The selected account's browser: online? run a job in it */}
      {accountFilter && <AccountBrowserBar accountId={accountFilter} username={selectedAccount?.reddit_username} />}

      {/* Dashboard Strip */}
      {renderDashboard()}

      

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #2a2a2a', marginBottom: '0' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={tabStyle(activeTab === tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card" style={{ borderRadius: '0 8px 8px 8px', marginTop: '0' }}>
        {analyticsLoading && activeTab !== 'posts' && activeTab !== 'pins' ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '24px' }}>Loading analytics...</p>
        ) : (
          <>
            {activeTab === 'subreddits' && renderSubredditsTab()}
            {activeTab === 'content' && renderContentTab()}
            {activeTab === 'sessions' && renderSessionsTab()}
            {activeTab === 'attempts' && renderAttemptsTab()}
            {activeTab === 'timing' && renderTimingTab()}
            {activeTab === 'posts' && renderPostsTab()}
            {activeTab === 'pins' && <PinsTab key={pinsNonce} creatorId={creatorId} accountId={accountFilter} onReadReddit={refreshFromReddit} readingReddit={redditRead} />}
          </>
        )}
      </div>

      {/* Exclude Subreddits Modal */}
      {showExcludeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.8)', zIndex: 1001,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '600px', maxHeight: '80vh',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0 }}>Exclude Subreddits</h2>
              <button onClick={() => setShowExcludeModal(false)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>
            <input placeholder="Search subreddits..." value={excludeSearch} onChange={e => setExcludeSearch(e.target.value)} autoFocus
              style={{ marginBottom: '12px', fontSize: '14px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #333', background: '#0f0f0f', color: '#fff' }} />
            {allTags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {allTags.map(tag => {
                  const selected = excludeTagFilter.includes(tag);
                  return (
                    <span key={tag} onClick={() => setExcludeTagFilter(prev => selected ? prev.filter(t => t !== tag) : [...prev, tag])}
                      style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', userSelect: 'none',
                        background: selected ? '#ff4500' : '#2a2a2a', color: selected ? '#ffffff' : '#aaaaaa',
                        border: selected ? '1px solid #ff4500' : '1px solid #444' }}>{tag}</span>
                  );
                })}
              </div>
            )}
            <div style={{ overflowY: 'auto', flex: 1, borderRadius: '8px', border: '1px solid #2a2a2a' }}>
              {filteredSubredditsForModal.length === 0 ? (
                <p style={{ color: '#555', textAlign: 'center', padding: '24px' }}>No subreddits found</p>
              ) : (
                filteredSubredditsForModal.map(sub => {
                  const isExcluded = pendingExcluded.includes(sub.name);
                  return (
                    <div key={sub.id} onClick={() => setPendingExcluded(prev => isExcluded ? prev.filter(s => s !== sub.name) : [...prev, sub.name])}
                      style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #2a2a2a',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: isExcluded ? '#2a0a0a' : 'transparent',
                        borderLeft: isExcluded ? '3px solid #ff4500' : '3px solid transparent' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: isExcluded ? '#ff4500' : '#ffffff' }}>{sub.name}</div>
                        {(sub.tags || []).length > 0 && (
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                            {(sub.tags || []).map(tag => (
                              <span key={tag} style={{ fontSize: '10px', background: '#2a2a2a', color: '#aaaaaa', padding: '2px 8px', borderRadius: '10px' }}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {sub.weekly_visitors && <span style={{ fontSize: '11px', color: '#555' }}>👀 {sub.weekly_visitors} weekly</span>}
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#555' }}>
                  {pendingExcluded.length > 0 ? `${pendingExcluded.length} excluded` : `${filteredSubredditsForModal.length} subreddits`}
                </span>
                {pendingExcluded.length > 0 && (
                  <button className="btn-secondary" style={{ fontSize: '11px', padding: '3px 10px' }}
                    onClick={() => setShowOnlyExcluded(!showOnlyExcluded)}>
                    {showOnlyExcluded ? 'Show All' : 'Show Excluded'}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-secondary" onClick={() => setShowExcludeModal(false)}>Cancel</button>
                <button onClick={confirmExclude}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    {showAIReports && (
        <AIReportsModal
          creatorId={creatorId}
          accountFilter={accountFilter}
          accounts={accounts}
          onClose={() => setShowAIReports(false)}
        />
      )}
    </div>
  );
}

export default Stats;